/**
 * Keeping the preview camera on the useful side of the simulated room.
 *
 * The room carries one surface at a time: a plank floor under a world scene or a flat image target, a
 * brick wall behind an upright one. Passing through either shows the scene from inside the scenery —
 * lit by nothing, with the content seen through the back of a plane — which reads as the preview being
 * broken rather than as a viewpoint anyone chose.
 *
 * TWO RULES, because a rotation and a translation have to be stopped differently:
 *
 *  - A rotation is bounded by handing OrbitControls a limit it already enforces itself, so a pointer
 *    drag stops short of the surface and its damping tail never carries past it. Stopping rotation by moving
 *    the camera afterwards would slide the pivot instead, turning an orbit into a pan.
 *  - A translation has no such limit, so it is corrected after the fact — camera and target together,
 *    exactly as a pan moves them, so the orbit radius and angle the user chose survive.
 *
 * Pure maths, no three, so the rule can be tested without a canvas.
 */

/**
 * How far short of a surface an orbit stops, as an angle.
 *
 * An angle rather than a distance because it is the only scale-invariant way to say "shortly before":
 * the two rooms are sized completely differently — a world scene's unit is a metre, the synthetic
 * camera's is half the target's printed height — so any fixed distance reads as a stride in one room
 * and a hair in the other. Held here, it is the same visible gap in both.
 *
 * The last few degrees are the ones worth refusing. Grazing a plane is where it stops being a surface
 * and becomes a bright line, the shadows flatten out, and the content is seen edge-on through it.
 */
export const ROOM_SURFACE_STANDOFF_RAD = (8 * Math.PI) / 180;

/**
 * How far inside a surface the camera is never allowed, in scene units.
 *
 * The backstop under the standoff above, and a different job: that one is the feel of the orbit, this
 * one is the promise that a PAN can never put the camera inside the scenery. Small and absolute,
 * because it is only ever the last resort.
 */
export const ROOM_SURFACE_MARGIN = 0.02;

/**
 * The largest polar angle that leaves the camera clear of a floor at `surfaceHeight`.
 *
 * OrbitControls measures polar from straight up, so the acos is the angle at which the orbit MEETS the
 * floor — how far below the target the floor sits, as a fraction of the orbit radius — and the orbit
 * stops `ROOM_SURFACE_STANDOFF_RAD` short of it.
 *
 * `unbounded` comes back whenever the floor cannot be reached from this orbit at all — a target more
 * than a radius above it — which is the common case when someone has zoomed right in, and is why this
 * returns a limit rather than a boolean. It is also the ceiling: a floor never widens the orbit past
 * the caller's own pole epsilon.
 */
export function maxPolarAboveSurface(targetHeight: number, surfaceHeight: number, radius: number, unbounded: number): number {
	if (!(radius > 0) || !Number.isFinite(targetHeight) || !Number.isFinite(surfaceHeight)) return unbounded;
	const cosine = (surfaceHeight - targetHeight) / radius;
	if (cosine <= -1) return unbounded;
	if (cosine >= 1) return 0;
	return Math.min(unbounded, Math.max(0, Math.acos(cosine) - ROOM_SURFACE_STANDOFF_RAD));
}

/**
 * How far the camera has gone past a surface, along that surface's normal, or 0 if it has not.
 *
 * One signed coordinate each, both measured along the normal: the floor's is height, the wall's is
 * depth. Add the result to the camera AND the target to undo the part of a pan that crossed over.
 */
export function surfaceOvershoot(cameraCoord: number, surfaceCoord: number, margin = ROOM_SURFACE_MARGIN): number {
	if (!Number.isFinite(cameraCoord) || !Number.isFinite(surfaceCoord)) return 0;
	const overshoot = surfaceCoord + margin - cameraCoord;
	return overshoot > 0 ? overshoot : 0;
}

/**
 * How far short of a WALL an orbit stops, as an angle — and much further short than a floor.
 *
 * ZERO, by choice: the orbit stops ON the wall's plane rather than short of it. That is a quarter turn
 * either way from square-on, and the full sweep from overhead to underneath — the most the promise
 * "never BEHIND the wall" allows, since the camera's depth `radius·sinφ·cosθ` reaches zero at the plane
 * and only goes negative past it.
 *
 * Earlier tries stood further off and were tuned by feel; the reason none of them read right is that the
 * build under test had no bound at all, so the comparison was always against unbounded orbiting. With
 * the bound actually live, the plane itself is where it was wanted.
 *
 * Raise it to buy clearance back — it is an angle, so it stays the same visible gap at every zoom and in
 * both rooms, and it governs BOTH axes: at `s`, left/right stops at `90° − s` and up/down spans
 * `s … 180° − s`. What it costs at zero is that the last few degrees are a grazing view along the wall,
 * where the wall is edge-on and the target on it is too.
 */
export const WALL_STANDOFF_RAD = 0;

/**
 * Kept off the poles regardless of the standoff, because that is a different problem from the wall.
 *
 * Straight up and straight down are where a look-at has no up vector to work from and the view flips —
 * so even a zero standoff, which otherwise allows the full sweep, stops a thousandth of a radian short.
 */
const POLE_EPSILON = 0.001;

/**
 * The polar angles a wall allows at all.
 *
 * The camera's depth in front of a wall standing through the target is `radius·sinφ·cosθ`, so the poles
 * put it ON the wall plane whatever the azimuth does — below `WALL_STANDOFF_RAD` of polar angle there is
 * no azimuth that stands off the wall at all, and the polar angle is where it has to be stopped. At a
 * zero standoff nothing is owed but the pole itself.
 */
export const MIN_POLAR_IN_FRONT_OF_WALL = Math.max(WALL_STANDOFF_RAD, POLE_EPSILON);
export const MAX_POLAR_IN_FRONT_OF_WALL = Math.PI - MIN_POLAR_IN_FRONT_OF_WALL;

/**
 * The azimuth limit that holds the camera clear of a wall at the polar angle it is currently at.
 *
 * `sinφ·cosθ` is the sine of the angle between the camera and the wall's plane, so holding it above
 * `sin(WALL_STANDOFF_RAD)` is exactly "stay that far off the wall", on the one axis OrbitControls can
 * enforce it on.
 *
 * A JOINT constraint expressed on one axis at a time, which is all `min/maxAzimuthAngle` can carry, so
 * it is re-derived from the polar angle at every use. Level with the target it comes to exactly
 * `90° − WALL_STANDOFF_RAD`; over the top it tightens, easing the camera round to the front rather than
 * letting it slide onto the wall.
 */
export function maxAzimuthInFrontOfWall(polar: number): number {
	const sinPolar = Math.sin(polar);
	if (!(sinPolar > 0)) return 0;
	const cosine = Math.sin(WALL_STANDOFF_RAD) / sinPolar;
	return cosine >= 1 ? 0 : Math.acos(cosine);
}

/**
 * Bring a camera direction back onto the cone a wall allows.
 *
 * POLAR FIRST, then the azimuth bound derived from the polar angle that survived it. The constraint
 * couples the two — `sinφ·cosθ` is what has to clear the wall — so bounding theta against the phi the
 * camera came FROM lets a diagonal move slip through the corner between them.
 *
 * Idempotent, which is what lets a caller enforce this every frame: a direction already in front of the
 * wall comes back unchanged, so a settled camera is never written to and never jitters.
 */
export function clampDirectionInFrontOfWall(polar: number, azimuth: number): { polar: number; azimuth: number } {
	// A non-finite pose would pass straight through Math.min/max as NaN, and this runs EVERY FRAME —
	// so one NaN would be written into the camera's position and stay there, blacking the canvas out
	// for good. Everywhere else a bad value dies with the gesture that produced it; here it would not.
	if (!Number.isFinite(polar) || !Number.isFinite(azimuth)) return { polar: Math.PI / 2, azimuth: 0 };
	const clampedPolar = Math.min(Math.max(polar, MIN_POLAR_IN_FRONT_OF_WALL), MAX_POLAR_IN_FRONT_OF_WALL);
	const limit = maxAzimuthInFrontOfWall(clampedPolar);
	return { polar: clampedPolar, azimuth: Math.min(Math.max(azimuth, -limit), limit) };
}
